/**
 * Divider
 *
 * Shared horizontal-rule component. Consolidates the previous
 * `SettingsDivider` (used by `MonetizationStep` / `ContentSettingsStep`)
 * and `ContactDivider` (used by `ContactPage` / `SupportPage`) behind a
 * single API so the two shapes can't drift.
 *
 * Two render modes, switched by the optional `margin` prop:
 *
 * - `margin` omitted (default — preserves the original `SettingsDivider`):
 *   outer wrapper at `responsive.spacing['3xl']` height with
 *   `alignItems: 'center'`, inner rule `responsive.divider.big` tall in
 *   `theme.border`. Used by the publish-flow step screens.
 *
 * - `margin` provided (preserves the original `ContactDivider`): a bare
 *   rule at `responsive.divider.big` × `100%` in `theme.separator` with
 *   the supplied vertical margin (`${margin}px 0` if numeric, pass-through
 *   if string). Used by `ContactPage` / `SupportPage`.
 */
import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

interface DividerProps {
  /**
   * Optional vertical margin. When provided, the component renders the
   * inline-rule shape (matches the former `ContactDivider`). When omitted,
   * it renders the wrapped shape inside a `responsive.spacing['3xl']`-tall
   * container (matches the former `SettingsDivider`).
   *
   * Numeric values are interpreted as pixels (`${margin}px 0`); strings
   * are passed through verbatim so callers can supply any valid CSS
   * margin shorthand.
   */
  margin?: number | string;
}

export const Divider: React.FC<DividerProps> = ({ margin }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  if (margin !== undefined) {
    const verticalMargin =
      typeof margin === 'number' ? `${margin}px 0` : margin;
    return (
      <div
        style={{
          width: '100%',
          height: responsive.divider.big,
          backgroundColor: theme.separator,
          margin: verticalMargin,
        }}
      />
    );
  }

  return (
    <div
      style={{
        height: responsive.spacing['3xl'],
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          width: '100%',
          height: responsive.divider.big,
          backgroundColor: theme.border,
        }}
      />
    </div>
  );
};
